import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, gbp, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ALL = "__ALL__";

export default function AdminEntries() {
  const [stores, setStores] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({ start_date: "", end_date: "", store_id: ALL, employee_id: ALL });
  const [data, setData] = useState({ entries: [], totals: { hours_worked: 0, total_wage: 0, deliveries_done: 0, delivery_amount: 0, count: 0 } });
  const [loading, setLoading] = useState(false);

  const loadStores = useCallback(async () => {
    try {
      const { data } = await api.get("/stores");
      setStores(data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadEmployees = useCallback(async (storeId) => {
    try {
      const params = storeId && storeId !== ALL ? { store_id: storeId } : {};
      const { data } = await api.get("/employees", { params });
      setEmployees(data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.store_id && filters.store_id !== ALL) params.store_id = filters.store_id;
      if (filters.employee_id && filters.employee_id !== ALL) params.employee_id = filters.employee_id;
      const { data } = await api.get("/entries", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadStores(); }, [loadStores]);
  useEffect(() => { loadEmployees(filters.store_id); }, [filters.store_id, loadEmployees]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const onStoreChange = (val) => setFilters((f) => ({ ...f, store_id: val, employee_id: ALL }));

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (filters.start_date) params.set("start_date", filters.start_date);
    if (filters.end_date) params.set("end_date", filters.end_date);
    if (filters.store_id && filters.store_id !== ALL) params.set("store_id", filters.store_id);
    if (filters.employee_id && filters.employee_id !== ALL) params.set("employee_id", filters.employee_id);
    const url = `${API}/entries/export.csv?${params.toString()}`;
    // Open with credentials by triggering same-window download
    window.open(url, "_blank");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/entries/${id}`);
      toast.success("Deleted");
      loadEntries();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const reset = () => setFilters({ start_date: "", end_date: "", store_id: ALL, employee_id: ALL });

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="label-uc">Reporting</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Daily entries</h1>
          <p className="text-sm text-zinc-500 mt-1">Filter, total and export labour data across all stores.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} className="rounded-none border-zinc-300" data-testid="filters-reset-button">Reset</Button>
          <Button onClick={exportCsv} className="rounded-none bg-zinc-950 hover:bg-zinc-800" data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 p-4 grid md:grid-cols-4 gap-3 mb-6" data-testid="filters-bar">
        <div>
          <Label className="label-uc">From</Label>
          <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            className="rounded-none border-zinc-300 mt-1.5 font-mono-data" data-testid="filter-start-date" />
        </div>
        <div>
          <Label className="label-uc">To</Label>
          <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            className="rounded-none border-zinc-300 mt-1.5 font-mono-data" data-testid="filter-end-date" />
        </div>
        <div>
          <Label className="label-uc">Store</Label>
          <Select value={filters.store_id} onValueChange={onStoreChange}>
            <SelectTrigger className="rounded-none border-zinc-300 mt-1.5" data-testid="filter-store-select">
              <SelectValue placeholder="All stores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All stores</SelectItem>
              {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-uc">Employee</Label>
          <Select value={filters.employee_id} onValueChange={(v) => setFilters({ ...filters, employee_id: v })}>
            <SelectTrigger className="rounded-none border-zinc-300 mt-1.5" data-testid="filter-employee-select">
              <SelectValue placeholder="All employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All employees</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Totals */}
      <div className="grid md:grid-cols-5 gap-0 border border-zinc-200 bg-white mb-6" data-testid="totals-bar">
        {[
          { k: "Entries", v: data.totals.count, mono: false, testid: "stat-count" },
          { k: "Hours worked", v: data.totals.hours_worked, mono: true, testid: "stat-hours" },
          { k: "Total wages", v: gbp(data.totals.total_wage), mono: true, testid: "stat-wage" },
          { k: "Deliveries", v: data.totals.deliveries_done, mono: true, testid: "stat-deliveries" },
          { k: "Delivery £", v: gbp(data.totals.delivery_amount), mono: true, testid: "stat-delivery-amount" },
        ].map((s, i) => (
          <div key={s.k} className={`p-4 ${i !== 0 ? "border-l border-zinc-200" : ""}`} data-testid={s.testid}>
            <div className="label-uc">{s.k}</div>
            <div className={`mt-1 text-2xl font-bold ${s.mono ? "font-mono-data" : "font-display"}`}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-entries-table">
            <thead className="bg-zinc-50">
              <tr className="text-left">
                <th className="px-3 py-2 label-uc">Date</th>
                <th className="px-3 py-2 label-uc">Store</th>
                <th className="px-3 py-2 label-uc">Employee</th>
                <th className="px-3 py-2 label-uc text-right">Hrs</th>
                <th className="px-3 py-2 label-uc text-right">Rate</th>
                <th className="px-3 py-2 label-uc text-right">Wage</th>
                <th className="px-3 py-2 label-uc text-right">Deliv.</th>
                <th className="px-3 py-2 label-uc text-right">Deliv. £</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="px-3 py-12 text-center text-zinc-400">Loading…</td></tr>}
              {!loading && data.entries.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-12 text-center text-zinc-400">No matching entries.</td></tr>
              )}
              {!loading && data.entries.map((e) => (
                <tr key={e.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`admin-entry-row-${e.id}`}>
                  <td className="px-3 py-2 font-mono-data">{e.date}</td>
                  <td className="px-3 py-2">{e.store_name}</td>
                  <td className="px-3 py-2">{e.employee_name}</td>
                  <td className="px-3 py-2 text-right font-mono-data">{e.hours_worked}</td>
                  <td className="px-3 py-2 text-right font-mono-data">{gbp(e.hourly_rate)}</td>
                  <td className="px-3 py-2 text-right font-mono-data font-semibold">{gbp(e.total_wage)}</td>
                  <td className="px-3 py-2 text-right font-mono-data">{e.deliveries_done}</td>
                  <td className="px-3 py-2 text-right font-mono-data">{gbp(e.delivery_amount)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => remove(e.id)} className="text-zinc-400 hover:text-[#DF1A22]" data-testid={`admin-entry-delete-${e.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
